import React, { Fragment } from 'react';
import { Accordion, Icon } from 'semantic-ui-react';
import { reduxForm, Field, submit, reset } from 'redux-form';
import { connect } from 'react-redux';
import _ from 'lodash';
import classNames from 'classnames';

import { Main } from 'Containers';
import { TitledCard } from 'Components';
import {
  CONTROL_INFO_MENUS_FORM_NAME,
  HACCP_TYPE_FORM_NAME,
  HACCP_FORM_NAME,
  RECIPE_GROUPS_FORM_NAME,
  INGREDIENT_GROUPS_FORM_NAME,
} from 'Common/FormEnum';
import { getIsDirty } from 'Common/Selectors/DirtySelector';
import { GroupedRadioField, CheckboxField } from 'Common/SemanticWrapper';
import {
  ControlInfoMenusFormEnum as Fields,
  ControlInfoHACCPFormEnum,
  ControlInfoHACCPTypeFormEnum,
  ControlInfoRecipeGroupsFormEnum,
  ControlInfoIngredientGroupsFormEnum,
  RecipeGroupFieldEnum,
  IngredientGroupFieldEnum,
} from 'Common/FieldEnum';
import { ApplyMenuBehaviorEnum } from 'Common/Constants/Enumeration/ApplyMenuBehaviorEnum';
import { createToast, toastType, errorToast } from 'Common/ToastHelper';

import { HACCPForm } from './HACCPForm';
import { HACCPTypeForm } from './HACCPTypeForm';
import { RecipeGroupsForm } from './RecipeGroupsForm';
import { IngredientGroupsForm } from './IngredientGroupsForm';
import { CNVersion } from './CNVersion';
import * as HACCPActions from '../state/haccp/actions';
import * as HACCPSelectors from '../state/haccp/selectors';
import * as RecipeGroupActions from '../state/recipeGroup/actions';
import * as RecipeGroupSelectors from '../state/recipeGroup/selectors';
import * as IngredientGroupActions from '../state/ingredientGroup/actions';
import * as IngredientGroupSelectors from '../state/ingredientGroup/selectors';
import * as CNVersionActions from '../state/CNVersionInformation/actions';
import * as CNVersionSelectors from '../state/CNVersionInformation/selectors';

const cycleMenuBehaviorOptions = [
  {
    label:
      'Apply the next available cycle day to the next available serving day.',
    value: ApplyMenuBehaviorEnum.APPLY_NEXT_AVAILABLE,
  },
  {
    label:
      'Skip the corresponding cycle day when there is a non-serving week day in the school calendar.',
    value: ApplyMenuBehaviorEnum.SKIP_CORRESPONDING_DAY,
  },
];

class MenusForm extends React.Component {
  state = {
    activeIndex: -1,
  };

  componentDidUpdate(prevProps) {
    const {
      savedHACCP: prevSavedHACCP,
      savedHACCPType: prevSavedHACCPType,
      deleteHACCP: prevDeleteHACCP,
      deleteHACCPType: prevDeleteHACCPType,
      deletedRecipeGroup: prevDeletedRecipeGroup,
      savedRecipeGroup: prevSavedRecipeGroup,
      savedIngredientGroup: prevSavedIngredientGroup,
      deletedIngredientGroup: prevDeletedIngredientGroup,
    } = prevProps;
    const {
      savedHACCP,
      savedHACCPType,
      clearHACCPError,
      deleteHACCP,
      deleteHACCPType,
      clearRecipeGroupError,
      deletedRecipeGroup,
      savedRecipeGroup,
      clearIngredientGroupError,
      savedIngredientGroup,
      deletedIngredientGroup,
    } = this.props;

    if (!prevSavedHACCP && savedHACCP) {
      createToast(toastType.success, 'Success', 'Saved HACCP', true);
    }

    if (!prevSavedHACCPType && savedHACCPType) {
      createToast(toastType.success, 'Success', 'Saved HACCP Type', true);
    }

    if (errorToast(this.props, prevProps, 'HACCPError')) {
      clearHACCPError();
    }

    if (!prevDeleteHACCP && deleteHACCP) {
      createToast(toastType.success, 'Success', 'Deleted HACCP', true);
    }

    if (!prevDeleteHACCPType && deleteHACCPType) {
      createToast(toastType.success, 'Success', 'Deleted HACCP Type', true);
    }

    if (!prevDeletedRecipeGroup && deletedRecipeGroup) {
      createToast(toastType.success, 'Success', 'Deleted Recipe Group', true);
    }

    if (errorToast(this.props, prevProps, 'recipeGroupError')) {
      clearRecipeGroupError();
    }

    if (!prevSavedRecipeGroup && savedRecipeGroup) {
      createToast(toastType.success, 'Success', 'Saved Recipe Group', true);
    }

    if (errorToast(this.props, prevProps, 'ingredientGroupError')) {
      clearIngredientGroupError();
    }

    if (!prevSavedIngredientGroup && savedIngredientGroup) {
      createToast(toastType.success, 'Success', 'Saved Ingredient Group', true);
    }

    if (!prevDeletedIngredientGroup && deletedIngredientGroup) {
      createToast(
        toastType.success,
        'Success',
        'Deleted Ingredient Group',
        true
      );
    }
  }

  handleAccordionClick = (e, titleProps) => {
    const { getIsFormDirty, showDirtyConfirm, dispatch } = this.props;
    const { activeIndex } = this.state;
    const { index } = titleProps;
    const newIndex = activeIndex === index ? -1 : index;

    let form = null;

    switch (activeIndex) {
      case 2:
        form = HACCP_TYPE_FORM_NAME;
        break;
      case 3:
        form = HACCP_FORM_NAME;
        break;
      case 4:
        form = RECIPE_GROUPS_FORM_NAME;
        break;
      case 5:
        form = INGREDIENT_GROUPS_FORM_NAME;
        break;
      default:
        form = null;
        break;
    }
    if (form) {
      const isDirty = getIsFormDirty(form);

      if (isDirty) {
        showDirtyConfirm({
          closeModalYesCallback: () => {
            this.setState({ activeIndex: newIndex });
            dispatch(reset(form));
          },
        });
        return;
      }
    }

    this.setState({ activeIndex: newIndex });
  };

  onHACCPSubmit = (values) => {
    const data = values[ControlInfoHACCPFormEnum.HACCP];
    this.props.writeHACCP(data);
  };

  onHACCPTypeSubmit = (values) => {
    const data = values[ControlInfoHACCPTypeFormEnum.HACCPType];
    this.props.writeHACCPType(data);
  };

  onRecipeGroupsSubmit = (values) => {
    let data = values[ControlInfoRecipeGroupsFormEnum.RecipeGroups];
    data = data.map((x) => ({
      ...x,
      [RecipeGroupFieldEnum.Description]: _.trim(
        x[RecipeGroupFieldEnum.Description]
      ),
    }));

    this.props.writeRecipeGroups(data);
  };

  onIngredientGroupsSubmit = (values) => {
    let data = values[ControlInfoIngredientGroupsFormEnum.IngredientGroups];
    data = data.map((x) => ({
      ...x,
      [IngredientGroupFieldEnum.Description]: _.trim(
        x[IngredientGroupFieldEnum.Description]
      ),
    }));

    this.props.writeIngredientGroups(data);
  };

  handleCNUpdateClick = () => {
    this.props.enqueueCNVersionUpdate();
  };

  handleCNPreviewClick = () => {
    this.props.enqueueCNPreview();
  };

  get mainProps() {
    const {
      mainProps,
      isReadOnly,
      loadingHACCPType,
      loadingHACCP,
      dispatch,
      loadingRecipeGroup,
      loadingIngredientGroup,
    } = this.props;

    const { activeIndex } = this.state;
    if (activeIndex <= 1) {
      return mainProps;
    }

    let form = HACCP_FORM_NAME;
    if (activeIndex === 2) {
      form = HACCP_TYPE_FORM_NAME;
    } else if (activeIndex === 3) {
      form = HACCP_FORM_NAME;
    } else if (activeIndex === 4) {
      form = RECIPE_GROUPS_FORM_NAME;
    } else if (activeIndex === 5) {
      form = INGREDIENT_GROUPS_FORM_NAME;
    }

    const isLoading =
      loadingIngredientGroup ||
      loadingRecipeGroup ||
      loadingHACCPType ||
      loadingHACCP;

    return {
      ...mainProps,
      toolbox: {
        rightButtons: [
          {
            content: 'Save',
            disabled: isLoading || isReadOnly,
            loading: isLoading,
            onClick: () => dispatch(submit(form)),
          },
        ],
        shown: true,
      },
    };
  }

  render() {
    const {
      isReadOnly,
      hide,
      cnVersionInformation,
      cnVersionUpdateEnqueueloading,
      cnUpdateLoading,
      cnPreviewLoading,
      cnPreviewEnqueuedLoading,
    } = this.props;
    const { activeIndex } = this.state;

    return (
      <div className={classNames({ hide: hide })}>
        <Main {...this.mainProps}>
          <TitledCard title="Settings">
            <Accordion fluid styled>
              <Accordion.Title
                active={activeIndex === 0}
                index={0}
                onClick={this.handleAccordionClick}
              >
                <Icon name="dropdown" />
                Apply Menu
              </Accordion.Title>
              <Accordion.Content active={activeIndex === 0}>
                <Field
                  label="When there is a non-serving week day in the applied date range"
                  component={GroupedRadioField}
                  name={Fields.ApplyCycleBehavior_MNU}
                  options={cycleMenuBehaviorOptions}
                />
              </Accordion.Content>

              <Accordion.Title
                active={activeIndex === 1}
                index={1}
                onClick={this.handleAccordionClick}
              >
                <Icon name="dropdown" />
                Inventory Integration
              </Accordion.Title>
              <Accordion.Content active={activeIndex === 1}>
                <Field
                  label="Update Inventory Usage"
                  component={CheckboxField}
                  name={Fields.UpdateINV_MNU}
                  useYN
                />
                <Field
                  label="Show Cost Per Serving and Inventory Item Codes on Recipe Prep Report"
                  component={CheckboxField}
                  name={Fields.ShowINV_MNU}
                  useYN
                />
              </Accordion.Content>

              <Accordion.Title
                active={activeIndex === 2}
                index={2}
                onClick={this.handleAccordionClick}
              >
                <Icon name="dropdown" />
                HACCP Type Setup
              </Accordion.Title>
              <Accordion.Content active={activeIndex === 2}>
                <HACCPTypeForm
                  onSubmit={this.onHACCPTypeSubmit}
                  isReadOnly={isReadOnly}
                />
              </Accordion.Content>
              <Accordion.Title
                active={activeIndex === 3}
                index={3}
                onClick={this.handleAccordionClick}
              >
                <Icon name="dropdown" />
                HACCP Setup
              </Accordion.Title>
              <Accordion.Content active={activeIndex === 3}>
                <HACCPForm
                  onSubmit={this.onHACCPSubmit}
                  isReadOnly={isReadOnly}
                />
              </Accordion.Content>
              <Accordion.Title
                active={activeIndex === 4}
                index={4}
                onClick={this.handleAccordionClick}
              >
                <Icon name="dropdown" />
                Recipe Groups Setup
              </Accordion.Title>
              <Accordion.Content active={activeIndex === 4}>
                <RecipeGroupsForm
                  onSubmit={this.onRecipeGroupsSubmit}
                  isReadOnly={isReadOnly}
                />
              </Accordion.Content>
              <Accordion.Title
                active={activeIndex === 5}
                index={5}
                onClick={this.handleAccordionClick}
              >
                <Icon name="dropdown" />
                Ingredient Groups Setup
              </Accordion.Title>
              <Accordion.Content active={activeIndex === 5}>
                <IngredientGroupsForm
                  onSubmit={this.onIngredientGroupsSubmit}
                  isReadOnly={isReadOnly}
                />
              </Accordion.Content>
              {cnVersionInformation.CurrentCNVersion && (
                <Fragment>
                  <Accordion.Title
                    active={activeIndex === 6}
                    index={6}
                    onClick={this.handleAccordionClick}
                  >
                    <Icon name="dropdown" />
                    CN Version Information
                  </Accordion.Title>
                  <Accordion.Content active={activeIndex === 6}>
                    <CNVersion
                      cnVersionInformation={cnVersionInformation}
                      handleUpdateClick={this.handleCNUpdateClick}
                      handleCNPreviewClick={this.handleCNPreviewClick}
                      cnVersionUpdateEnqueueloading={
                        cnVersionUpdateEnqueueloading
                      }
                      cnPreviewEnqueuedLoading={cnPreviewEnqueuedLoading}
                      updateInProgress={cnUpdateLoading}
                      previewInProgress={cnPreviewLoading}
                    />
                  </Accordion.Content>
                </Fragment>
              )}
            </Accordion>
          </TitledCard>
        </Main>
      </div>
    );
  }
}

MenusForm = reduxForm({
  form: CONTROL_INFO_MENUS_FORM_NAME,
})(MenusForm);

const mapStateToProps = (state) => ({
  savedHACCP: HACCPSelectors.getSavedHACCP(state),
  savedHACCPType: HACCPSelectors.getSavedHACCPType(state),
  HACCPError: HACCPSelectors.getError(state),
  deleteHACCP: HACCPSelectors.getDeleteHACCP(state),
  deleteHACCPType: HACCPSelectors.getDeleteHACCPType(state),
  loadingHACCPType: HACCPSelectors.getLoadingHACCPType(state),
  loadingHACCP: HACCPSelectors.getLoadingHACCP(state),
  cnVersionUpdateEnqueueloading: CNVersionSelectors.getCNVersionUpdateEnqueueLoading(
    state
  ),
  cnPreviewEnqueuedLoading: CNVersionSelectors.getCNPreviewEnqueuedLoading(
    state
  ),

  recipeGroupError: RecipeGroupSelectors.getRecipeGroupError(state),
  loadingRecipeGroup: RecipeGroupSelectors.getRecipeGroupLoading(state),
  savedRecipeGroup: RecipeGroupSelectors.getRecipeGroupSaved(state),
  deletedRecipeGroup: RecipeGroupSelectors.getRecipeGroupDeleted(state),

  ingredientGroupError: IngredientGroupSelectors.getIngredientGroupError(state),
  loadingIngredientGroup: IngredientGroupSelectors.getIngredientGroupLoading(
    state
  ),
  savedIngredientGroup: IngredientGroupSelectors.getIngredientGroupSaved(state),
  deletedIngredientGroup: IngredientGroupSelectors.getIngredientGroupDeleted(
    state
  ),
  cnUpdateLoading: CNVersionSelectors.getCNVersionUpdateLoading(state),
  cnPreviewLoading: CNVersionSelectors.getCNPreviewLoading(state),

  getIsFormDirty: (form) => getIsDirty(form)(state),
});

const mapDispatchToProps = {
  writeHACCP: HACCPActions.writeHACCP,
  writeHACCPType: HACCPActions.writeHACCPType,
  clearHACCPError: HACCPActions.clearError,

  writeRecipeGroups: RecipeGroupActions.writeRecipeGroup,
  clearRecipeGroupError: RecipeGroupActions.clearError,

  writeIngredientGroups: IngredientGroupActions.writeIngredientGroup,
  clearIngredientGroupError: IngredientGroupActions.clearError,
  enqueueCNVersionUpdate: CNVersionActions.enqueueCNVersionUpdate,
  enqueueCNPreview: CNVersionActions.enqueueCNPreview,
};

MenusForm = connect(mapStateToProps, mapDispatchToProps)(MenusForm);

export { MenusForm };



// WEBPACK FOOTER //
// ./src/pages/controlinformation/components/MenusForm.js